import React, { useState } from "react";
import { collection, addDoc } from "firebase/firestore";
import "./OtherPages.css";

/* ══════════════ GUESTS ══════════════ */
export function GuestsPage({ guests }) {
  return (
    <section className="page-section">
      <div className="page-inner">
        <div className="section-label">Featured</div>
        <h2 className="section-title">Show Guests</h2>
        {guests.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">👥</div><p>Guest profiles will appear here.</p></div>
        ) : (
          <div className="guests-grid">
            {guests.map((g) => (
              <div key={g.id} className="guest-card">
                {g.photoUrl ? (
                  <img src={g.photoUrl} className="guest-avatar" alt={g.name} />
                ) : (
                  <div className="guest-avatar guest-avatar--placeholder">👤</div>
                )}
                <div className="guest-name">{g.name}</div>
                {g.title && <div className="guest-title">{g.title}</div>}
                <div className="guest-bio">{g.bio}</div>
                {g.episodeTitle && (
                  <div className="guest-episode">EP: {g.episodeTitle}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ══════════════ LIVE ══════════════ */
export function LivePage({ liveData, getYTEmbed }) {
  const embed = liveData.streamUrl ? getYTEmbed(liveData.streamUrl) : null;
  return (
    <section className="page-section">
      <div className="page-inner">
        <div className="section-label">Broadcast</div>
        <h2 className="section-title">{liveData.isLive ? "🔴 On Air Now" : "Live Stream"}</h2>

        {liveData.isLive && embed ? (
          <>
            <iframe className="live-embed" src={`${embed}?autoplay=1`} allow="autoplay; encrypted-media" allowFullScreen title="Live Stream" />
            {liveData.chatUrl && (
              <iframe src={liveData.chatUrl} className="live-chat" title="Live Chat" />
            )}
          </>
        ) : (
          <div className="live-offline">
            <div style={{ fontSize: "4rem" }}>📡</div>
            <h3>Not Currently Live</h3>
            <p>We're off air right now. Stay tuned for the next broadcast.</p>
          </div>
        )}

        {(liveData.nextShowDate || liveData.nextShowTitle) && (
          <div className="next-show">
            <h4>Next Show</h4>
            {liveData.nextShowTitle && <p className="next-show-title">{liveData.nextShowTitle}</p>}
            {liveData.nextShowDate && <p>{new Date(liveData.nextShowDate).toLocaleString()}</p>}
          </div>
        )}
      </div>
    </section>
  );
}

/* ══════════════ ABOUT ══════════════ */
export function AboutPage({ aboutData: d }) {
  const SOCIALS = [
    { key: "youtube",   label: "YouTube",   icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.75 15.5V8.5l6.5 3.5-6.5 3.5z"/></svg> },
    { key: "twitter",   label: "X / Twitter", icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
    { key: "instagram", label: "Instagram", icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
    { key: "facebook",  label: "Facebook",  icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
    { key: "spotify",   label: "Spotify",   icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg> },
  ].filter((s) => d[s.key]);

  return (
    <section className="page-section">
      <div className="page-inner">
        <div className="about-grid">
          <div className="about-img-wrap">
            {d.photoUrl ? (
              <img src={d.photoUrl} className="about-photo" alt={d.name} />
            ) : (
              <div className="about-photo about-photo--placeholder">🎙</div>
            )}
            <div className="about-badge">
              <span className="about-badge-num">{d.yearsExperience || "20+"}</span>
              <span className="about-badge-lbl">Years<br />Experience</span>
            </div>
          </div>

          <div className="about-text">
            <div className="section-label">Your Host</div>
            <h2 className="about-name">{d.name || "Kedrick Scribner"} <span className="about-dot">.</span></h2>
            <p>{d.bio || "Kedrick Scribner is a former United States Marine and former law enforcement officer who has dedicated his life to truth, justice, and service."}</p>
            <p>{d.bio2 || "Through Talk News, Kedrick tackles trending topics — from law enforcement conduct and criminal justice reform to politics, community affairs, and world events."}</p>
            <div className="credentials">
              <span className="credential">⭐ Former U.S. Marine</span>
              <span className="credential">🚔 Former Law Enforcement</span>
              <span className="credential">🎙 Talk Show Host</span>
              <span className="credential">🇺🇸 American Patriot</span>
            </div>
            {SOCIALS.length > 0 && (
              <div className="social-links">
                {SOCIALS.map((s) => (
                  <a
                    key={s.key}
                    href={d[s.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link social-link--icon"
                    title={s.label}
                  >
                    {s.icon}
                    <span>{s.label}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


/* ══════════════ CONTACT ══════════════ */
export function ContactPage({ db, showToast }) {
  const blank = { name: "", email: "", subject: "", message: "" };
  const [form,     setForm]     = useState(blank);
  const [email,    setEmail]    = useState("");
  const [sending,  setSending]  = useState(false);

  const sendMsg = async () => {
    if (!form.name || !form.email || !form.message) return showToast("Please fill in all required fields");
    setSending(true);
    await addDoc(collection(db, "messages"), { ...form, createdAt: Date.now() });
    showToast("Message sent ✓");
    setForm(blank);
    setSending(false);
  };

  const subscribe = async () => {
    if (!email || !email.includes("@")) return showToast("Please enter a valid email");
    await addDoc(collection(db, "subscribers"), { email, createdAt: Date.now() });
    showToast("You're subscribed ✓");
    setEmail("");
  };

  return (
    <section className="page-section">
      <div className="page-inner">
        <div className="section-label">Get In Touch</div>
        <h2 className="section-title">Contact & Newsletter</h2>

        <div className="contact-grid">
          {/* Contact form */}
          <div>
            <div className="contact-sub-label">Send a Message</div>
            <div className="field-group"><label className="field-label">Name *</label><input className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></div>
            <div className="field-group"><label className="field-label">Email *</label><input className="field-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" /></div>
            <div className="field-group"><label className="field-label">Subject</label><input className="field-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Guest inquiry, feedback…" /></div>
            <div className="field-group"><label className="field-label">Message *</label><textarea className="field-input field-textarea" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message…" /></div>
            <button className="btn-submit" onClick={sendMsg} disabled={sending}>{sending ? "Sending…" : "Send Message"}</button>
          </div>

          {/* Newsletter */}
          <div>
            <div className="newsletter-box">
              <h3>Stay Informed</h3>
              <p>Subscribe to Talk News and never miss an episode, live stream, or breaking story.</p>
              <div className="newsletter-row">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" onKeyDown={(e) => e.key === "Enter" && subscribe()} />
                <button onClick={subscribe}>Subscribe</button>
              </div>
            </div>

            <div className="guest-inquiry">
              <div className="contact-sub-label">Want to Be a Guest?</div>
              <p>Are you an expert, activist, official, or community leader with a story to tell? Reach out via the contact form and mention your interest in being a guest on the show.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
